import * as React from 'react'
import { CompanyType } from '@/app/constants/companyType'
import { isAdmin } from 'app/services/local/roleService'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useQueryCompanies } from '@/modules/shared/query-client/useQueryCompanies'
import {
  ServiceAccountUser,
  useQueryMyServiceAccounts,
  useQueryServiceAccountUsers,
} from '@/modules/shared/query-client/useServiceAccountAdmin'

/**
 * Lista użytkowników firmy usługowej: dostęp do całego konta daje roster, a nie rola na
 * samym koncie. Roster czyta tylko global admin i właściciel konta - dla pozostałych nie
 * pytamy wcale (endpoint dałby 403), więc plakietek rostera nie widzą.
 */
export function useServiceAccountUsersContext(companyId?: string) {
  const { data: companies } = useQueryCompanies()
  const company = companies?.find((c) => String(c.id) === String(companyId))
  const isService = company?.type === CompanyType.SERVICE
  const { data: myAccounts } = useQueryMyServiceAccounts()
  const canManage =
    isService &&
    (isAdmin() ||
      !!myAccounts?.some((a) => String(a.id) === String(companyId) && a.isOwner))
  const { data: roster } = useQueryServiceAccountUsers(
    canManage && companyId ? companyId : null,
  )
  const rosterById = React.useMemo(
    () => new Map((roster ?? []).map((user) => [user.userId, user])),
    [roster],
  )
  return { isService, canManage, rosterById }
}

/** Komórka roli w firmie usługowej: plakietka z rostera + role na koncie, bez edycji. */
export const ServiceAccountRoleCell: React.FC<{
  roles: Array<{ id: number; name: string }>
  membership?: ServiceAccountUser
}> = ({ roles, membership }) => {
  const { t } = useLocalize()

  if (!membership && roles.length === 0) {
    return (
      <span className="tw-text-sm tw-italic tw-text-content-muted">
        {t('administrationPanel-companies.roles-in-child-companies')}
      </span>
    )
  }

  return (
    <div className="tw-inline-flex tw-flex-wrap tw-gap-1">
      {membership && (
        <span className="badge badge-sm badge-primary">
          {t(
            membership.isOwner
              ? 'administrationPanel-companies.account-wide-owner'
              : 'administrationPanel-companies.account-wide-member',
          )}
        </span>
      )}
      {roles.map((role) => (
        <span key={role.id} className="badge badge-sm badge-accent">
          {role.name}
        </span>
      ))}
    </div>
  )
}

/** Pasek nad tabelą użytkowników firmy usługowej. */
export const ServiceAccountUsersNotice: React.FC<{ href?: string }> = ({ href }) => {
  const { t } = useLocalize()
  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-b tw-border-base-300 tw-bg-base-200 tw-px-4 tw-py-2 tw-text-sm">
      <span>{t('administrationPanel-companies.service-account-users-hint')}</span>
      {href && (
        <a href={href} className="tw-text-primary hover:tw-underline">
          {t('administrationPanel-companies.manage-service-account')}
        </a>
      )}
    </div>
  )
}
